import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { store } from './store/store';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { LandingPage } from './pages/LandingPage';
import { DashboardPage } from './pages/DashboardPage';
import { TransfersPage } from './pages/TransfersPage';
import { NewTransferPage } from './pages/NewTransferPage';
import { EditTransferPage } from './pages/EditTransferPage';
import OrganizationsPage from './pages/OrganizationsPage';
import OrganizationFormPage from './pages/OrganizationFormPage';
import DemoEmailPage from './pages/DemoEmailPage';
import DriversPage from './pages/DriversPage';
import DriverFormPage from './pages/DriverFormPage';
import ProvidersPage from './pages/ProvidersPage';
import ProviderFormPage from './pages/ProviderFormPage';
import { ProviderPaymentsPage } from './pages/ProviderPaymentsPage';
import { AdminProviderPaymentsPage } from './pages/AdminProviderPaymentsPage';
import UsersPage from './pages/UsersPage';
import UserFormPage from './pages/UserFormPage';
import { VehicleTypesPage } from './pages/VehicleTypesPage';
import { ProfilePage } from './pages/ProfilePage';
import PricingPage from './pages/PricingPage';
import ProviderUsersPage from './pages/ProviderUsersPage';
import ProviderTransfersPage from './pages/ProviderTransfersPage';
import { FormSubmissionsPage } from './pages/FormSubmissionsPage';
import { NotificationsPage } from './pages/NotificationsPage';
import CustomersPage from './pages/CustomersPage';
import CustomerFormPage from './pages/CustomerFormPage';
import { AdminCustomerPaymentsPage } from './pages/AdminCustomerPaymentsPage';
import ProviderGroupsPage from './pages/ProviderGroupsPage';
import PoolMemberPaymentsPage from './pages/PoolMemberPaymentsPage';
import AdminPoolMemberPaymentsPage from './pages/AdminPoolMemberPaymentsPage';
import PlatformUsersPage from './pages/PlatformUsersPage';
import { ErrorBoundary } from './components/ErrorBoundary';

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function App() {
  return (
    <ErrorBoundary>
    <Provider store={store}>
      <GoogleOAuthProvider clientId={googleClientId}>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* Protected Routes with Layout */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="transfers" element={<TransfersPage />} />
            <Route path="transfers/new" element={<NewTransferPage />} />
            <Route path="transfers/:id" element={<EditTransferPage />} />
            <Route path="organizations" element={<OrganizationsPage />} />
            <Route path="organizations/new" element={<OrganizationFormPage />} />
            <Route path="organizations/:id" element={<OrganizationFormPage />} />
            <Route path="demo-email" element={<DemoEmailPage />} />
            <Route path="drivers" element={<DriversPage />} />
            <Route path="drivers/new" element={<DriverFormPage />} />
            <Route path="drivers/:id" element={<DriverFormPage />} />
            <Route path="providers" element={<ProvidersPage />} />
            <Route path="providers/new" element={<ProviderFormPage />} />
            <Route path="providers/:id" element={<ProviderFormPage />} />
            <Route path="provider-payments" element={<ProviderPaymentsPage />} />
            <Route path="admin/provider-payments" element={<AdminProviderPaymentsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="users/new" element={<UserFormPage />} />
            <Route path="users/:id" element={<UserFormPage />} />
            <Route path="vehicle-types" element={<VehicleTypesPage />} />
            <Route path="pricing" element={<PricingPage />} />
            <Route path="profile" element={<ProfilePage />} />
            {/* Provider Users */}
            <Route path="providers/:providerId/users" element={<ProviderUsersPage />} />
            {/* Form Submissions (for Manager+) */}
            <Route path="form-submissions" element={<FormSubmissionsPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            {/* Provider Transfers (for Provider Manager/User) */}
            <Route path="provider/transfers" element={<ProviderTransfersPage />} />
            {/* Customers */}
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/new" element={<CustomerFormPage />} />
            <Route path="customers/:id" element={<CustomerFormPage />} />
            <Route path="admin/customer-payments" element={<AdminCustomerPaymentsPage />} />
            <Route path="provider-groups" element={<ProviderGroupsPage />} />
            <Route path="pool/payments" element={<PoolMemberPaymentsPage />} />
            <Route path="admin/pool-member-payments" element={<AdminPoolMemberPaymentsPage />} />
            <Route path="platform/users" element={<PlatformUsersPage />} />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </BrowserRouter>
      </GoogleOAuthProvider>
    </Provider>
    </ErrorBoundary>
  );
}

export default App;
